import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import { DefaultChevron, IChevron } from 'src/Definitions';
import { ChevronDto, Client, IChevronDto, SwaggerResponse } from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';
import NotificationStore from '../NotificationStore';
import ObservationsStore from '../ObservationsStore';

const ColumnSettingsDto = 'ChevronDto';

export default class ChevronsStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public chevrons: IChevron[];
    @observable
    public selectedChevron?: IChevron;
    public dataTableStore: DataTableStore<IChevronDto, IChevron>;
    private observationStoresByBehavior: Map<string, ObservationsStore>;

    constructor(
        private globalStore: GlobalStore,
        private client: Client,
        private notificationStore: NotificationStore
    ) {
        this.observationStoresByBehavior = new Map<string, ObservationsStore>();
        this.dataTableStore = new DataTableStore<IChevronDto, IChevron>(
            globalStore,
            ({ filter, page, orderBy, includeInactives }) =>
                client.getAllChevrons(filter, page, undefined, orderBy, includeInactives),
            (Chevron) => this.setupChevrons(Chevron),
            (userId, columns) =>
                client.apiColumnSettingsByUserIdByTypePut(userId, ColumnSettingsDto, columns)
        );
    }

    @action.bound
    public getObservationStoreForBehavior(behaviorId: string): ObservationsStore {
        if (this.observationStoresByBehavior.has(behaviorId)) {
            return this.observationStoresByBehavior.get(behaviorId)!;
        } else {
            const newObservationStore = new ObservationsStore(
                this.globalStore,
                this.client,
                behaviorId
            );
            this.observationStoresByBehavior.set(behaviorId, newObservationStore);
            return newObservationStore;
        }
    }

    @action.bound
    public disableChevron(chevron: IChevron) {
        this.isLoading = true;
        return this.client
            .archiveChevron(chevron.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getColumnSettingsAndChevrons() {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.dataTableStore.loadData();
                });
            })
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    @action.bound
    public getAllChevrons(pageNumber?: number, pageSize?: number) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        this.isLoading = true;
        return this.client
            .getAllChevrons(undefined, pageNumber, pageSize)
            .then((resp: SwaggerResponse<IChevronDto[]>) =>
                runInAction(() => {
                    this.chevrons = this.setupChevrons(resp.result);
                })
            )
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    @action.bound
    public recoverChevron(chevron: IChevron) {
        this.isLoading = true;
        return this.client
            .reactivateChevron(chevron.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    @action.bound
    public saveChevron(chevron: IChevron) {
        this.isLoading = true;
        let promise;
        const chevronDto = new ChevronDto(chevron);
        if (chevron.isNew) {
            chevronDto.isActive = true;
            promise = this.client.createChevron(chevronDto);
        } else {
            promise = this.client.updateChevron(chevron.id, chevronDto);
        }
        return promise
            .then(() => {})
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public sendNotification(key: string, message: string) {
        this.notificationStore.sendNotification(key, message);
    }

    private setupChevrons = (chevrons: IChevronDto[]) => {
        return chevrons.map((x) => this.setupChevronMenuItems(this.addDefaultFields(x)));
    };

    private addDefaultFields = (chevron: IChevronDto): IChevron => {
        return { ...DefaultChevron(), ...chevron };
    };

    private setupChevronMenuItems = (chevron: IChevron) => {
        chevron.menuItems = [];
        if (chevron.isActive) {
            chevron.menuItems.push({
                isConfirm: true,
                name: 'Disable',
                onClick: () => this.disableChevron(chevron),
            });
        } else {
            chevron.menuItems.push({
                isConfirm: true,
                name: 'Recover',
                onClick: () => this.recoverChevron(chevron),
            });
        }
        return chevron;
    };
}



// WEBPACK FOOTER //
// ./src/stores/ChevronStore/ChevronsStore.ts